/**
 * @file Composant de connexion via WebAuthn (passkey).
 * Permet à l'utilisateur de s'authentifier avec une clé d'accès enregistrée
 * (Touch ID, Windows Hello, clé de sécurité FIDO2, etc.) sans mot de passe.
 *
 * Flux :
 * 1. POST /auth/webauthn/login/begin/    → options PublicKeyCredentialRequestOptions
 * 2. navigator.credentials.get(options)   → assertion
 * 3. POST /auth/webauthn/login/complete/  → session authentifiée
 *
 * @see R-0044
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { Button } from './ui/button';
import { Loader2, Fingerprint } from 'lucide-react';
import { getErrorMessage } from '../lib/errorMessages';
import { prepareAuthenticationOptions, serializeAuthenticationCredential } from '../lib/webauthn';

/**
 * Composant de connexion par passkey WebAuthn.
 * Affiche un bouton "Sign in with passkey" qui déclenche le flux d'authentification
 * WebAuthn complet. Appelle `onSuccess()` lorsque l'authentification réussit.
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback invoqué après connexion réussie via passkey.
 * @returns {JSX.Element}
 */
export default function WebAuthnLogin({ onSuccess }) {
  const { t } = useTranslation();
  /* ── État de chargement et erreurs ── */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Lance le flux complet d'authentification WebAuthn en trois étapes :
   * 1. Récupère les options d'assertion depuis le backend
   * 2. Appelle l'API Web Authentication du navigateur (prompt biométrique / clé de sécurité)
   * 3. Envoie la réponse d'assertion au backend pour finaliser la connexion
   */
  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      /* ── Étape 1 : récupérer les options du backend ── */
      const beginRes = await api.post('/auth/webauthn/login/begin/');
      const options = prepareAuthenticationOptions(beginRes.data);

      /* ── Étape 2 : appeler l'API Web Authentication du navigateur ── */
      const credential = await navigator.credentials.get({ publicKey: options });

      /* ── Étape 3 : envoyer l'assertion au backend ── */
      await api.post('/auth/webauthn/login/complete/', {
        credential: serializeAuthenticationCredential(credential),
      });

      onSuccess();
    } catch (e) {
      /* Distinguer les erreurs utilisateur (annulation) des erreurs techniques */
      if (e.name === 'NotAllowedError') {
        setError(t('login.passkey_cancelled'));
      } else if (e?.response || e?.code === 'ERR_NETWORK') {
        setError(getErrorMessage(e, t('login.passkey_failed')));
      } else {
        setError(t('login.passkey_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="webauthn-login">
      {/* ── Message d'erreur ── */}
      {error && (
        <div className="mb-3 p-3 text-sm text-red-600 border border-red-200 rounded-sm bg-red-50" data-testid="webauthn-login-error">
          {error}
        </div>
      )}

      {/* ── Bouton de connexion par passkey ── */}
      <Button
        type="button"
        variant="outline"
        className="w-full rounded-sm"
        onClick={handleLogin}
        disabled={loading}
        data-testid="webauthn-login-btn"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Fingerprint className="h-4 w-4 mr-2" />
            {t('login.passkey_submit')}
          </>
        )}
      </Button>
    </div>
  );
}
